import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { Sparkles, FileText, Loader2, RotateCcw } from "lucide-react";
import { enhanceCoverLetterForAssignment, fetchEmployeeCoverLettersForAdmin } from "../api/TheirStackJobsApi";
import { listCoverLetterDocuments, CoverLetterEnhanceResult } from "features/cover-letter/api/CoverLetterApi";
import EnhancedCoverLetterModal from "features/cover-letter/ui/EnhancedCoverLetterModal";
import CoverLetterEnhancementLogsModal from "./CoverLetterEnhancementLogsModal";

type Phase = "idle" | "enhancing" | "done" | "error";

interface CoverLetterEnhancementPatch {
  has_cover_letter_enhancement: boolean;
  cover_letter_id_enhanced: number;
  enhanced_cover_letter_data: any;
  has_cover_letter_enhancement_logs: boolean;
}

export interface CoverLetterEnhancementSectionHandle {
  runEnhancement: () => Promise<void>;
  canEnhance: boolean;
}

interface Props {
  assignmentId: number;
  isAdmin: boolean;
  employeeCode?: string; // admin must supply
  initialEnhancedData?: CoverLetterEnhanceResult | null;
  coverLetterIdEnhanced?: number | null;
  hasEnhancementLogs?: boolean;
  jobTitle: string;
  companyName?: string | null;
  onEnhanced?: (patch: CoverLetterEnhancementPatch) => void;
  /** category_id of the CV currently selected in the sibling CvEnhancementSection
   * — drives the auto-match default. */
  autoMatchCategoryId?: number | null;
  /** The CV currently selected in the sibling CvEnhancementSection — passed through
   * to the enhance-cover-letter call so grounding doesn't depend on the (much slower)
   * CV pipeline having already written its result back when both run concurrently via
   * a combined "Enhance All" trigger. */
  groundingCvId?: number | null;
  showOwnTrigger?: boolean;
}

const CoverLetterEnhancementSection = forwardRef<CoverLetterEnhancementSectionHandle, Props>(
  function CoverLetterEnhancementSection({
    assignmentId,
    isAdmin,
    employeeCode,
    initialEnhancedData,
    coverLetterIdEnhanced,
    hasEnhancementLogs,
    jobTitle,
    companyName,
    onEnhanced,
    autoMatchCategoryId,
    groundingCvId,
    showOwnTrigger = true,
  }, ref) {
  const [phase, setPhase] = useState<Phase>(initialEnhancedData ? "done" : "idle");
  const [coverLetterList, setCoverLetterList] = useState<{ id: number; title: string; category_id?: number | null }[] | null>(null);
  const [clLoading, setClLoading] = useState(false);
  const [selectedCoverLetterId, setSelectedCoverLetterId] = useState<number | "">("");
  const [manuallySet, setManuallySet] = useState(false);
  const [enhancedData, setEnhancedData] = useState<CoverLetterEnhanceResult | null>(initialEnhancedData ?? null);
  const [showModal, setShowModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [hasLogs, setHasLogs] = useState(hasEnhancementLogs ?? false);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => { return () => { mountedRef.current = false; }; }, []);

  useEffect(() => {
    if (initialEnhancedData && (phase === "idle" || phase === "error")) {
      setEnhancedData(initialEnhancedData);
      setPhase("done");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEnhancedData]);

  useEffect(() => {
    if (isAdmin && !employeeCode) return;
    setClLoading(true);
    const load = isAdmin
      ? fetchEmployeeCoverLettersForAdmin(employeeCode!)
      : listCoverLetterDocuments().then((docs) => docs.map((d) => ({ id: d.id, title: d.title, category_id: d.category_id })));
    load
      .then((list) => { if (mountedRef.current) { setCoverLetterList(list); setClLoading(false); } })
      .catch(() => { if (mountedRef.current) { setCoverLetterList([]); setClLoading(false); } });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, employeeCode]);

  // Auto-match: pick the most-recently-updated cover letter sharing the selected
  // CV's category_id, unless the user has manually chosen one themselves.
  useEffect(() => {
    if (manuallySet || phase !== "idle") return;
    if (!coverLetterList || coverLetterList.length === 0) return;
    if (autoMatchCategoryId == null) return;
    const match = coverLetterList.find((c) => c.category_id === autoMatchCategoryId);
    if (match) setSelectedCoverLetterId(match.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMatchCategoryId, coverLetterList, manuallySet, phase]);

  async function runEnhancement(coverLetterId: number) {
    if (!coverLetterId) return;
    if (isAdmin && !employeeCode) return;
    setPhase("enhancing");
    setError(null);
    try {
      const result = await enhanceCoverLetterForAssignment(
        assignmentId,
        coverLetterId,
        isAdmin,
        isAdmin ? employeeCode : undefined,
        groundingCvId,
      );
      if (mountedRef.current) {
        setEnhancedData(result.enhanced_cover_letter_data as CoverLetterEnhanceResult);
        setPhase("done");
        if (result.has_cover_letter_enhancement_logs) setHasLogs(true);
        onEnhanced?.({
          has_cover_letter_enhancement: true,
          cover_letter_id_enhanced: coverLetterId,
          enhanced_cover_letter_data: result.enhanced_cover_letter_data,
          has_cover_letter_enhancement_logs: result.has_cover_letter_enhancement_logs ?? hasLogs,
        });
      }
    } catch (err: any) {
      if (mountedRef.current) {
        setError(err?.response?.data?.detail ?? "Enhancement failed. Please try again.");
        setPhase("error");
      }
    }
  }

  async function reEnhance() {
    const coverLetterId = (selectedCoverLetterId as number) || coverLetterIdEnhanced;
    if (!coverLetterId) return;
    await runEnhancement(coverLetterId);
  }

  const canEnhance = selectedCoverLetterId !== "" && (!isAdmin || !!employeeCode);

  useImperativeHandle(ref, () => ({
    runEnhancement: () => runEnhancement(selectedCoverLetterId as number),
    canEnhance,
  }));

  if (phase === "done" && enhancedData) {
    return (
      <>
        <div className="flex items-center gap-2 flex-wrap">
          <FileText className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
          <button
            onClick={() => setShowModal(true)}
            className="text-xs text-emerald-400 hover:text-emerald-300 font-inter font-medium underline underline-offset-2 transition-colors"
          >
            View Enhanced Cover Letter
          </button>
          {hasLogs && (
            <button
              onClick={() => setShowLogsModal(true)}
              className="text-xs text-muted-foreground hover:text-primary font-inter underline underline-offset-2 transition-colors"
            >
              Enhancement logs
            </button>
          )}
          <button
            onClick={reEnhance}
            title="Re-enhance"
            className="flex items-center gap-1 px-1.5 py-0.5 text-muted-foreground hover:text-primary rounded transition-colors"
          >
            <RotateCcw className="w-3 h-3 shrink-0" />
            <span className="text-[10px] font-inter underline underline-offset-2">Re-Enhance</span>
          </button>
        </div>
        {showModal && (
          <EnhancedCoverLetterModal
            enhancedContent={enhancedData.content}
            overallScore={enhancedData.overall_score}
            critique={enhancedData.critique}
            topPriorityFix={enhancedData.top_priority_fix}
            jobTitle={jobTitle}
            companyName={companyName}
            isAdmin={isAdmin}
            employeeCode={isAdmin ? employeeCode : undefined}
            onClose={() => setShowModal(false)}
          />
        )}
        {showLogsModal && (
          <CoverLetterEnhancementLogsModal
            assignmentId={assignmentId}
            isAdmin={isAdmin}
            onClose={() => setShowLogsModal(false)}
          />
        )}
      </>
    );
  }

  if (phase === "enhancing") {
    return (
      <div className="flex items-center gap-2 text-xs text-muted-foreground font-inter">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-primary shrink-0" />
        Enhancing cover letter…
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="flex items-center gap-1.5 text-xs text-primary font-inter font-medium shrink-0">
          <Sparkles className="w-3.5 h-3.5 shrink-0" />
          {showOwnTrigger ? "Enhance cover letter for this job" : "Cover Letter"}
        </span>
        <select
          value={String(selectedCoverLetterId)}
          onChange={(e) => { setManuallySet(true); setSelectedCoverLetterId(e.target.value ? Number(e.target.value) : ""); }}
          disabled={clLoading || (isAdmin && !employeeCode)}
          className="bg-surface-muted border border-border rounded-lg px-2 py-1 text-xs text-foreground font-inter cursor-pointer hover:border-border focus:outline-none focus:border-primary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <option value="">
            {clLoading
              ? "Loading…"
              : isAdmin && !employeeCode
              ? "— no employee linked —"
              : coverLetterList?.length === 0
              ? "No cover letters found"
              : "Select Cover Letter (Optional)"}
          </option>
          {(coverLetterList ?? []).map((cl) => (
            <option key={cl.id} value={String(cl.id)}>
              {cl.title}
            </option>
          ))}
        </select>
        {showOwnTrigger && (
          <button
            onClick={() => runEnhancement(selectedCoverLetterId as number)}
            disabled={!canEnhance}
            className="px-2.5 py-1 bg-primary hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-primary-foreground text-xs font-inter font-medium rounded-lg transition-colors shrink-0"
          >
            Enhance
          </button>
        )}
      </div>
      {error && <p className="text-[11px] text-destructive font-inter mt-1">{error}</p>}
    </>
  );
});

export default CoverLetterEnhancementSection;
